/**
 * tone-tokens.ts
 * ─────────────────────────────────────────────────────────────────
 * Tokens UI tone-aware pour composants stubs génériques utilisés par
 * toutes les audiences (hors particuliers qui ont leur kit dédié).
 *
 * 3 tones supportés :
 *  - BRAND (emerald) : Particuliers, Professionnels, Collectivités, Copropriétés
 *  - ACCENT (lime)   : Artisans, Agriculteurs
 *  - CORPORATE (stone): Entreprises, Tertiaire, Industrie
 *
 * Consommé par AideAudienceStubPage, AudienceParcoursStubPage, et tout
 * composant éditorial multi-audiences.
 */

import type { AudienceId } from '@shared/constants/audiences';
import { audienceLegacyTone } from '../../../config/audience-theme';

export type StubTone = 'brand' | 'accent' | 'corporate';

export interface StubToneTokens {
  /** Chip eyebrow (couleur + ring) */
  eyebrowChip: string;
  /** Halo radial discret derrière hero (rgba via tailwind class) */
  heroHalo: string;
  /** Texte de gradient pour montants vedette / promesses */
  gradientText: string;
  /** Lien actif (breadcrumb hover, cross-links) */
  linkColor: string;
  /** Background icône carrée */
  iconChipBg: string;
  iconChipText: string;
  iconChipRing: string;
  /** Bordure tonale subtile (vue side card "montant") */
  sideCardRing: string;
  /** Check-icon couleur (Conditions) */
  checkColor: string;
  /** Coup d'œil ombrage CTA primaire */
  ctaShadow: string;
  /** Variant `Button` à utiliser pour CTA primaire */
  buttonVariant: 'primary' | 'brand' | 'accent';
  /** Fond du panneau CTA final (sur fond ink) */
  ctaFinalAccentHalo: string;

  /* ─── Tokens "rich layout" (héros pleine largeur + CTA immersif) ─── */
  /** Spot-glow central derrière le hero (utilitaire global) */
  spotGlow: 'spot-glow' | 'spot-glow-accent' | 'spot-glow-cool';
  /** Halo flou top-right du hero (chaleureux) */
  heroHaloTopRight: string;
  /** Halo flou bottom-left du hero (énergique) */
  heroHaloBottomLeft: string;
  /** Hairline gradient au sommet des cards */
  cardTopHairline: string;
  /** Gradient appliqué au mot-accent du H1 (fond clair) */
  headlineGradient: string;
  /** Gradient appliqué au mot-accent du H2 sur fond sombre (CTA immersif) */
  headlineGradientDark: string;
  /** Variant `Button` coloré à utiliser sur fond sombre (CTA final immersif) */
  ctaButtonDarkVariant: 'brand' | 'accent';
  /** Halo top-right du CTA final immersif (fort, blur) */
  ctaHaloTopRight: string;
  /** Halo bottom-left du CTA final immersif (fort, blur) */
  ctaHaloBottomLeft: string;
  /** Couleur du live-dot de l'eyebrow dark (CTA immersif) */
  darkDotClass: string;
}

const BRAND: StubToneTokens = {
  eyebrowChip: 'bg-brand-100 text-brand-800',
  heroHalo: 'bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(16,185,129,0.06),transparent)]',
  gradientText: 'bg-gradient-to-br from-brand-700 via-brand-600 to-accent-600 bg-clip-text text-transparent',
  linkColor: 'text-brand-700 hover:text-brand-800',
  iconChipBg: 'bg-brand-50',
  iconChipText: 'text-brand-700',
  iconChipRing: 'ring-brand-200/60',
  sideCardRing: 'ring-brand-200/50',
  checkColor: 'text-brand-500',
  ctaShadow: 'shadow-cta-brand hover:shadow-cta-brand-hover',
  buttonVariant: 'primary',
  ctaFinalAccentHalo: 'bg-brand-500/30',
  spotGlow: 'spot-glow',
  heroHaloTopRight: 'absolute -right-32 -top-32 h-[480px] w-[480px] rounded-full bg-brand-100/40 blur-[120px]',
  heroHaloBottomLeft: 'absolute -left-24 bottom-[-180px] h-[420px] w-[420px] rounded-full bg-accent-100/40 blur-[120px]',
  cardTopHairline: 'bg-gradient-to-r from-transparent via-brand-300/50 to-transparent',
  headlineGradient: 'bg-gradient-to-r from-brand-600 via-brand-500 to-accent-500 bg-clip-text text-transparent',
  headlineGradientDark: 'bg-gradient-to-r from-accent-300 via-brand-300 to-brand-200 bg-clip-text text-transparent',
  ctaButtonDarkVariant: 'brand',
  ctaHaloTopRight: 'pointer-events-none absolute -right-32 -top-40 h-[480px] w-[480px] rounded-full bg-accent-500/30 blur-[140px]',
  ctaHaloBottomLeft: 'pointer-events-none absolute -left-24 bottom-[-120px] h-[420px] w-[420px] rounded-full bg-brand-500/30 blur-[120px]',
  darkDotClass: 'bg-accent-400',
};

const ACCENT: StubToneTokens = {
  eyebrowChip: 'bg-accent-100 text-accent-800',
  heroHalo: 'bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(132,204,22,0.08),transparent)]',
  gradientText: 'bg-gradient-to-br from-accent-700 via-accent-600 to-brand-600 bg-clip-text text-transparent',
  linkColor: 'text-accent-700 hover:text-accent-800',
  iconChipBg: 'bg-accent-50',
  iconChipText: 'text-accent-700',
  iconChipRing: 'ring-accent-200/60',
  sideCardRing: 'ring-accent-200/60',
  checkColor: 'text-accent-600',
  ctaShadow: 'shadow-cta-accent hover:shadow-cta-accent-hover',
  buttonVariant: 'accent',
  ctaFinalAccentHalo: 'bg-accent-400/30',
  spotGlow: 'spot-glow-accent',
  heroHaloTopRight: 'absolute -right-32 -top-32 h-[480px] w-[480px] rounded-full bg-accent-100/45 blur-[120px]',
  heroHaloBottomLeft: 'absolute -left-24 bottom-[-180px] h-[420px] w-[420px] rounded-full bg-brand-100/35 blur-[120px]',
  cardTopHairline: 'bg-gradient-to-r from-transparent via-accent-300/55 to-transparent',
  headlineGradient: 'bg-gradient-to-r from-accent-600 via-accent-500 to-brand-500 bg-clip-text text-transparent',
  headlineGradientDark: 'bg-gradient-to-r from-accent-200 via-accent-300 to-brand-200 bg-clip-text text-transparent',
  ctaButtonDarkVariant: 'accent',
  ctaHaloTopRight: 'pointer-events-none absolute -right-32 -top-40 h-[480px] w-[480px] rounded-full bg-accent-400/35 blur-[140px]',
  ctaHaloBottomLeft: 'pointer-events-none absolute -left-24 bottom-[-120px] h-[420px] w-[420px] rounded-full bg-brand-500/25 blur-[120px]',
  darkDotClass: 'bg-accent-400',
};

const CORPORATE: StubToneTokens = {
  eyebrowChip: 'bg-stone-100 text-ink',
  heroHalo: 'bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(28,25,23,0.05),transparent)]',
  gradientText: 'bg-gradient-to-br from-ink via-stone-700 to-brand-700 bg-clip-text text-transparent',
  linkColor: 'text-stone-700 hover:text-ink',
  iconChipBg: 'bg-stone-100',
  iconChipText: 'text-stone-700',
  iconChipRing: 'ring-stone-200/60',
  sideCardRing: 'ring-stone-200/60',
  checkColor: 'text-stone-600',
  ctaShadow: 'shadow-cta-ink hover:shadow-cta-ink-hover',
  buttonVariant: 'primary',
  ctaFinalAccentHalo: 'bg-stone-500/30',
  spotGlow: 'spot-glow-cool',
  heroHaloTopRight: 'absolute -right-32 -top-32 h-[480px] w-[480px] rounded-full bg-stone-200/50 blur-[120px]',
  heroHaloBottomLeft: 'absolute -left-24 bottom-[-180px] h-[420px] w-[420px] rounded-full bg-brand-100/30 blur-[120px]',
  cardTopHairline: 'bg-gradient-to-r from-transparent via-stone-300/60 to-transparent',
  headlineGradient: 'bg-gradient-to-r from-ink via-stone-700 to-brand-700 bg-clip-text text-transparent',
  headlineGradientDark: 'bg-gradient-to-r from-brand-200 via-brand-300 to-accent-200 bg-clip-text text-transparent',
  ctaButtonDarkVariant: 'brand',
  ctaHaloTopRight: 'pointer-events-none absolute -right-32 -top-40 h-[480px] w-[480px] rounded-full bg-brand-500/25 blur-[140px]',
  ctaHaloBottomLeft: 'pointer-events-none absolute -left-24 bottom-[-120px] h-[420px] w-[420px] rounded-full bg-stone-500/25 blur-[120px]',
  darkDotClass: 'bg-brand-400',
};

const TONE_MAP: Record<StubTone, StubToneTokens> = {
  brand: BRAND,
  accent: ACCENT,
  corporate: CORPORATE,
};

/**
 * Convertit un AudienceId vers le tone "stub" canonique.
 * Diffère légèrement de `audienceLegacyTone` car on ne supporte que 3 tones
 * (brand/accent/corporate) côté stubs B2B/B2G.
 */
export function audienceStubTone(id: AudienceId): StubTone {
  const legacy = audienceLegacyTone(id);
  if (legacy === 'sage') return 'corporate';
  if (legacy === 'accent') return 'accent';
  return 'brand';
}

export function getStubTokens(id: AudienceId): StubToneTokens {
  return TONE_MAP[audienceStubTone(id)];
}

export function getStubTokensByTone(tone: StubTone): StubToneTokens {
  return TONE_MAP[tone];
}

/** Helper pour les chemins de breadcrumb par audience */
export function getAudienceBreadcrumbLabel(id: AudienceId): string {
  const labels: Record<AudienceId, string> = {
    particuliers: 'Particuliers',
    professionnels: 'Professionnels',
    entreprises: 'Entreprises',
    tertiaire: 'Tertiaire',
    industrie: 'Industrie',
    collectivites: 'Collectivités',
    coproprietes: 'Copropriétés',
    artisans: 'Artisans RGE',
    agriculteurs: 'Agriculteurs',
  };
  return labels[id];
}

/** Source canonique du site (pour SEO JSON-LD) */
export const SITE_URL = 'https://solutionecofrance.fr';
